import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import io from 'socket.io-client';

const SocketContext = createContext();

export const useSocketContext = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocketContext must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const { user } = useSelector((state) => ({ ...state }));
  const socketRef = useRef(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionAttempts, setConnectionAttempts] = useState(0);

  useEffect(() => {
    if (user?.token && !socketRef.current) {
      // Auto-detect backend URL: use env var if set, otherwise detect from current location
      let backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      if (!backendUrl || backendUrl === 'http://localhost:8000') {
        // In production, use same origin (relative URL) or detect from window
        if (typeof window !== 'undefined') {
          const hostname = window.location.hostname;
          
          // Check if it's a production domain (not localhost)
          // This works for usmaapp.com, socialmedia.testatozas.in, and any other production domains
          if (hostname !== 'localhost' && hostname !== '127.0.0.1' && !hostname.startsWith('192.168.')) {
            // Production: use same origin for socket (socket.io will handle it automatically)
            // Empty string means same origin - socket.io will use current window.location
            backendUrl = '';
            // console.log(`🌐 Production domain detected: ${hostname}, using same origin for socket`);
          } else {
            // Development: use localhost:8000
            backendUrl = 'http://localhost:8000';
            console.log(`💻 Development mode: using ${backendUrl} for socket`);
          }
        } else {
          backendUrl = 'http://localhost:8000';
        }
      } else {
        console.log(`🔧 Using configured REACT_APP_BACKEND_URL: ${backendUrl}`);
      }
      
      console.log('🔌 Connecting to socket server:', backendUrl || `same origin (${window.location.origin})`);
      
      socketRef.current = io(backendUrl || undefined, {
        auth: {
          token: user.token,
        },
        withCredentials: true,
        transports: ['websocket', 'polling'],
        timeout: 20000,
        forceNew: false,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });

      // Connection event listeners
      socketRef.current.on('connect', () => {
        // console.log('✅ Socket connected:', socketRef.current.id);
        setIsConnected(true);
        setConnectionAttempts(0);
        
        // Join user room immediately after connection
        if (user.id) {
          const userId = String(user.id); // Normalize to string
          socketRef.current.emit('join', userId);
          // console.log('🔌 Joined user room:', userId);
        }
      });
      
      // Handle reconnection - rejoin room
      socketRef.current.on('reconnect', (attemptNumber) => {
        console.log('✅ Reconnected to server after', attemptNumber, 'attempts');
        setIsConnected(true);
        // Rejoin user-specific room after reconnection
        if (user?.id) {
          const userId = String(user.id);
          socketRef.current.emit('join', userId);
          console.log(`Rejoined user room for user ${userId} after reconnect`);
        }
      });

      socketRef.current.on('disconnect', (reason) => {
        console.log('❌ Socket disconnected:', reason);
        setIsConnected(false);
        
        // Only increment attempts if it's not a manual disconnect
        if (reason !== 'io client disconnect') {
          setConnectionAttempts(prev => prev + 1);
        }
      });

      socketRef.current.on('connect_error', (error) => {
        console.error('❌ Socket connection error:', error);
        setIsConnected(false);
      });

      socketRef.current.on('error', (error) => {
        console.error('❌ Socket error:', error);
      });

      // Make socket available globally
      window.notificationSocket = socketRef.current;
    }

    // Cleanup function
    return () => {
      if (socketRef.current) {
        console.log('🔌 Disconnecting socket');
        socketRef.current.disconnect();
        socketRef.current = null;
        window.notificationSocket = null;
        setIsConnected(false);
      }
    };
  }, [user?.token, user?.id]);

  // Disconnect when user logs out
  useEffect(() => {
    if (!user?.token && socketRef.current) {
      console.log('🔌 User logged out, disconnecting socket');
      socketRef.current.disconnect();
      socketRef.current = null;
      window.notificationSocket = null;
      setIsConnected(false);
    }
  }, [user?.token]);

  const value = {
    socket: socketRef.current,
    isConnected,
    connectionAttempts,
    reconnect: () => {
      if (socketRef.current) {
        socketRef.current.connect();
      }
    }
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
}; 